(() => {
  const formatMoney = (cents, currency) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(cents / 100);

  /*
   * Sold-out / pre-launch panel. Replaces the dead "sold out" button with a way forward:
   * switch to an available option, be notified, or reserve.
   */
  if (!customElements.get('cvb-stock-panel')) {
    customElements.define('cvb-stock-panel', class extends HTMLElement {
      connectedCallback() {
        const dataTag = this.querySelector('script[type="application/json"]');
        if (!dataTag) return;

        this.data = JSON.parse(dataTag.textContent);
        this.form = document.getElementById(this.getAttribute('form-id'));
        this.wrapper = this.closest('.product-form__buy-buttons');
        this.titleElement = this.querySelector('[data-stock-title]');
        this.linkElement = this.querySelector('[data-stock-link]');
        this.alternatives = this.querySelector('[data-stock-alternatives]');
        this.alternativesList = this.querySelector('[data-stock-alternatives-list]');
        this.similarButton = this.querySelector('[data-stock-similar]');

        this.onVariantChanged = (event) => this.render(event.detail.variant);
        this.form?.addEventListener('variant:changed', this.onVariantChanged);

        this.alternativesList?.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-variant-id]');
          if (chip) this.selectVariant(chip.dataset.variantId);
        });

        const recommendations = document.querySelector('.shopify-section--product-recommendations');
        if (this.similarButton && recommendations) {
          this.similarButton.hidden = false;
          this.similarButton.addEventListener('click', () => recommendations.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }

        this.render(this.data.variants.find((variant) => variant.id === this.data.selected) || null);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('variant:changed', this.onVariantChanged);
      }

      get isPreLaunch() {
        return this.dataset.mode === 'prelaunch';
      }

      render(variant) {
        const isAvailable = Boolean(variant && variant.available);

        this.hidden = isAvailable;
        this.wrapper?.classList.toggle('is-unavailable', !isAvailable);
        if (isAvailable) return;

        const hasVariants = this.data.variants.length > 1;
        const variantTitle = variant && hasVariants ? (variant.public_title || variant.title) : '';
        const productTitle = this.dataset.productTitle;
        const subject = variantTitle ? `"${productTitle}" (${variantTitle})` : `"${productTitle}"`;

        let title;
        let message;

        if (this.isPreLaunch) {
          title = 'Reserve antes do lançamento';
          message = `Olá! Quero reservar a peça ${subject} — pré-lançamento.`;
        } else {
          title = variantTitle ? `Esgotado em ${variantTitle}` : 'Esta peça esgotou';
          message = `Olá! Quero ser avisada quando a peça ${subject} voltar ao estoque.`;
        }

        if (this.titleElement) this.titleElement.textContent = title;
        if (this.linkElement) {
          this.linkElement.href = `https://api.whatsapp.com/send/?phone=${this.dataset.whatsapp}&text=${encodeURIComponent(message)}`;
        }

        this.renderAlternatives(variant);
        this.updateLabel();
      }

      renderAlternatives(variant) {
        if (!this.alternatives || !this.alternativesList) return;

        const available = this.data.variants.filter((item) => item.available && (!variant || item.id !== variant.id));
        this.alternatives.hidden = available.length === 0;
        this.alternativesList.replaceChildren(...available.map((item) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'cvb-stock__chip';
          chip.dataset.variantId = item.id;
          chip.textContent = item.title;
          return chip;
        }));
      }

      selectVariant(variantId) {
        const variant = this.data.variants.find((item) => String(item.id) === String(variantId));
        const scope = this.closest('.product-form');
        if (!variant || !scope) return;

        let lastControl = null;

        variant.options.forEach((value, index) => {
          const controls = scope.querySelectorAll(`.product-form__variants [name="option${index + 1}"]`);

          controls.forEach((control) => {
            if (control.tagName === 'SELECT') {
              control.value = value;
              lastControl = control;
            } else if (control.value === value) {
              control.checked = true;
              lastControl = control;
            }
          });
        });

        lastControl?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // The theme rewrites the label to "sold out" on every variant change, which is wrong for a pre-launch piece
      updateLabel() {
        if (!this.isPreLaunch) return;

        const labelList = this.closest('.product__info')?.querySelector('[data-product-label-list]');
        if (labelList) labelList.innerHTML = '<span class="label label--custom">Pré-lançamento</span>';
      }
    });
  }

  /*
   * "Compre junto" set builder: pick complementary pieces (with their own size) and add everything in one go.
   */
  if (!customElements.get('cvb-bundle')) {
    customElements.define('cvb-bundle', class extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.getAttribute('form-id'));
        this.mainItem = this.querySelector('[data-bundle-main]');
        this.countElement = this.querySelector('[data-bundle-count]');
        this.totalElement = this.querySelector('[data-bundle-total]');
        this.addButton = this.querySelector('[data-bundle-add]');
        this.errorElement = this.querySelector('[data-bundle-error]');
        this.currency = this.dataset.currency;

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-bundle-add]')) return this.add();
          if (event.target.closest('a, select')) return;

          const item = event.target.closest('[data-bundle-item]');
          if (item && !item.hasAttribute('data-bundle-main')) this.toggle(item);
        });

        this.addEventListener('change', (event) => {
          const select = event.target.closest('[data-bundle-select]');
          if (!select) return;

          const item = select.closest('[data-bundle-item]');
          const option = select.selectedOptions[0];
          item.dataset.variantId = option.value;
          item.dataset.price = option.dataset.price;
          item.querySelector('[data-bundle-price]').textContent = formatMoney(Number(option.dataset.price), this.currency);
          if (!item.classList.contains('is-selected')) this.toggle(item);
          this.update();
        });

        this.onVariantChanged = (event) => this.updateMain(event.detail.variant);
        this.form?.addEventListener('variant:changed', this.onVariantChanged);

        this.update();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('variant:changed', this.onVariantChanged);
      }

      toggle(item) {
        const isSelected = item.classList.toggle('is-selected');
        item.querySelector('[data-bundle-toggle]')?.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        this.update();
      }

      updateMain(variant) {
        if (!this.mainItem) return;

        const isAvailable = Boolean(variant && variant.available);
        this.mainItem.classList.toggle('is-selected', isAvailable);
        this.mainItem.classList.toggle('is-unavailable', !isAvailable);

        if (variant) {
          this.mainItem.dataset.variantId = variant.id;
          this.mainItem.dataset.price = variant.price;
          this.mainItem.querySelector('[data-bundle-price]').textContent = formatMoney(variant.price, this.currency);

          const variantTitle = this.mainItem.querySelector('[data-bundle-variant-title]');
          if (variantTitle) variantTitle.textContent = variant.public_title || '';
        }

        this.update();
      }

      get selectedItems() {
        return [...this.querySelectorAll('[data-bundle-item].is-selected')];
      }

      update() {
        const selected = this.selectedItems;
        const addOns = selected.filter((item) => !item.hasAttribute('data-bundle-main'));
        const total = selected.reduce((sum, item) => sum + Number(item.dataset.price), 0);

        this.addButton.disabled = addOns.length === 0;
        this.totalElement.textContent = addOns.length > 0 ? formatMoney(total, this.currency) : '';
        this.countElement.textContent = addOns.length > 0
          ? `${selected.length} ${selected.length === 1 ? 'peça' : 'peças'} no conjunto`
          : 'Toque nas peças para montar seu conjunto';
        this.errorElement.hidden = true;
      }

      async add() {
        const items = this.selectedItems.map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
        if (items.length === 0) return;

        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items, sections: ['mini-cart'] })
          });
          const added = await response.json();

          if (!response.ok) {
            this.errorElement.textContent = added.description || 'Não foi possível adicionar o conjunto. Tente novamente.';
            this.errorElement.hidden = false;
            return;
          }

          const cart = await (await fetch(`${window.themeVariables.routes.cartUrl}.js`)).json();
          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));

          cart.sections = added.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cart, openMiniCart: window.themeVariables.settings.cartType === 'drawer' }
          }));

          if (window.themeVariables.settings.cartType === 'page') window.location.href = window.themeVariables.routes.cartUrl;
        } catch (error) {
          this.errorElement.textContent = 'Não foi possível adicionar o conjunto. Tente novamente.';
          this.errorElement.hidden = false;
        } finally {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = this.selectedItems.filter((item) => !item.hasAttribute('data-bundle-main')).length === 0;
        }
      }
    });
  }
})();
